import { type LivestockGenderT } from '@ruminati/types_v2/livestock_shared'

export enum CattleAgeClass {
  // Typically 0-6/8 months
  CALF_UNWEANED = 'CALF_UNWEANED',

  // Typically 6/8-12 months
  WEANERS = 'WEANERS',

  // 12+ months, may be joined in systems
  YEARLINGS = 'YEARLINGS',

  // Mostly likely in Northern systems, 24-30 months (not yet calved)
  HEIFER_24_30M = 'HEIFER_24_30M',

  // Ocassionally in Northern systems for slow joiners, 30-36 months (not yet calved)
  HEIFER_30_36M = 'HEIFER_30_36M',

  // Has had 1 calf, but still growing
  COW_FIRST_CALF = 'COW_FIRST_CALF',

  // 2+ calvings
  COW_MATURE = 'COW_MATURE',

  // ... that have been castrated
  STEER_18_24M = 'STEER_18_24M',

  // Less Common - 24-36 months that have been castrated, that are being finished to heavy
  STEER_24_36M = 'STEER_24_36M',

  // At maturity
  BULL_BREEDING = 'BULL_BREEDING',
}

export const CATTLE_AGE_CLASS_LABELS: Record<CattleAgeClass, string> = {
  [CattleAgeClass.CALF_UNWEANED]: 'Calves',
  [CattleAgeClass.WEANERS]: 'Weaners',
  [CattleAgeClass.YEARLINGS]: 'Yearlings',
  [CattleAgeClass.HEIFER_24_30M]: 'Heifers (24-30m)',
  [CattleAgeClass.HEIFER_30_36M]: 'Heifers (30-36m)',
  [CattleAgeClass.STEER_18_24M]: 'Steers (18-24m)',
  [CattleAgeClass.STEER_24_36M]: 'Heavy Steers',
  [CattleAgeClass.COW_FIRST_CALF]: 'Cows - 1st calf',
  [CattleAgeClass.COW_MATURE]: 'Cows - mature',
  [CattleAgeClass.BULL_BREEDING]: 'Bulls - breeding',
}

export function getCattleGenderAgeClassLabel(
  gender: LivestockGenderT,
  ageClass: CattleAgeClass,
): string {
  if (ageClass === CattleAgeClass.CALF_UNWEANED && gender === 'female') {
    return 'Weaner Calves'
  }
  if (ageClass === CattleAgeClass.WEANERS && gender === 'female') {
    return 'Weaner Heifers'
  } else if (ageClass === CattleAgeClass.WEANERS && gender === 'male') {
    return 'Weaner Steers'
  }
  if (ageClass === CattleAgeClass.YEARLINGS && gender === 'female') {
    return 'Yearling Heifers'
  } else if (ageClass === CattleAgeClass.YEARLINGS && gender === 'male') {
    return 'Yearling Steers'
  }
  return CATTLE_AGE_CLASS_LABELS[ageClass]
}

export const CATTLE_AGE_CLASS_ORDER = {
  [CattleAgeClass.CALF_UNWEANED]: 0,
  [CattleAgeClass.WEANERS]: 1,
  [CattleAgeClass.YEARLINGS]: 2,
  [CattleAgeClass.HEIFER_24_30M]: 5,
  [CattleAgeClass.HEIFER_30_36M]: 7,
  [CattleAgeClass.STEER_18_24M]: 4,
  [CattleAgeClass.STEER_24_36M]: 6,
  [CattleAgeClass.COW_FIRST_CALF]: 3,
  [CattleAgeClass.COW_MATURE]: 8,
  [CattleAgeClass.BULL_BREEDING]: 9,
}

// Rates in $/kg liveweight (lwt), derived from MLA NLRS API 4-month averages (Jan–Apr 2025).
// Source: https://api-mlastatistics.mla.com.au/report/5?indicatorID=<n>
// All MLA indicators are in c/kg lwt for cattle, so conversion is simply: c/kg ÷ 100.
// Where no direct indicator exists, a discount/premium multiplier is applied to the closest indicator.
//
// Indicator mapping:
//   CALF_UNWEANED  — National Young Cattle #14 (~375c) × 0.85 discount
//   WEANERS        — National Restocker Yearling Steer #2 (~407c)
//   YEARLINGS      — National Young Cattle #14 (~375c)
//   HEIFER_24_30M  — National Restocker Yearling Heifer #12 (~310c)
//   HEIFER_30_36M  — National Feeder Heifer #17 (~316c)
//   STEER_18_24M   — National Feeder Steer #3 (~370c)
//   STEER_24_36M   — National Heavy Steer #4 (~360c)
//   COW_FIRST_CALF — National Processor Cow #13 (~290c) × 1.10 premium
//   COW_MATURE     — National Processor Cow #13 (~290c)
//   BULL_BREEDING  — National Heavy Steer #4 (~360c) × 1.10 premium
export const CATTLE_DEFAULT_VALUATION_RATES: Record<CattleAgeClass, number> = {
  [CattleAgeClass.CALF_UNWEANED]: 3.19, // $/kg lwt - Young Cattle #14 × 0.85
  [CattleAgeClass.WEANERS]: 4.07, // $/kg lwt - Restocker Yearling Steer #2
  [CattleAgeClass.YEARLINGS]: 3.75, // $/kg lwt - Young Cattle #14
  [CattleAgeClass.HEIFER_24_30M]: 3.1, // $/kg lwt - Restocker Yearling Heifer #12
  [CattleAgeClass.HEIFER_30_36M]: 3.16, // $/kg lwt - Feeder Heifer #17
  [CattleAgeClass.STEER_18_24M]: 3.7, // $/kg lwt - Feeder Steer #3
  [CattleAgeClass.STEER_24_36M]: 3.6, // $/kg lwt - Heavy Steer #4
  [CattleAgeClass.COW_FIRST_CALF]: 3.19, // $/kg lwt - Processor Cow #13 × 1.10
  [CattleAgeClass.COW_MATURE]: 2.9, // $/kg lwt - Processor Cow #13
  [CattleAgeClass.BULL_BREEDING]: 3.96, // $/kg lwt - Heavy Steer #4 × 1.10
}
